import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useRequestState } from "@app/hooks";
import { selectRequestDetailState } from "../selector";
import { requestDetails } from "../slice";

//--------------------------------------
// Helper Components
//--------------------------------------
const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    spacing={2}
    sx={{ py: 0.5 }}
  >
    <Typography
      variant="body1"
      sx={{ fontWeight: 600, color: "text.secondary", minWidth: "120px" }}
    >
      {label} :
    </Typography>
    <Typography
      variant="body1"
      sx={{
        color: "text.primary",
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Stack>
);

const InfoCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Paper
    sx={{
      p: 3,
      borderRadius: 2,
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      flex: 1, // important for equal height
      minHeight: "320px", // adjust as you like
    }}
  >
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
        {title}
      </Typography>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  </Paper>
);

//--------------------------------------
// Main Component
//--------------------------------------
export const RequestDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    data,
    status: requestStatus,
    error,
  } = useRequestState({
    stateSelector: selectRequestDetailState,
    errorShown: true,
  });

  useEffect(() => {
    if (id) {
      dispatch(requestDetails({ id: Number(id) }));
    }
  }, [dispatch, id]);

  const request = data?.data || {};
  const user = request?.user || {};
  const car = request?.car || {};
  const assignedSalesperson = request?.assignedSalesperson || {};
console.log('requesttt>>>', request);

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Request Details
        </Typography>
      </Stack>

      {/* Responsive equal-height grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          alignItems: "stretch", // ensures equal height
        }}
      >
        {/* LEFT COLUMN CARDS */}
        <Stack spacing={3} sx={{ height: "100%" }}>
          <InfoCard title="Request Information">
            <InfoRow label="Request ID" value={id} />
            <InfoRow label="Type" value={request?.type || "N/A"} />
            <InfoRow
              label="Date"
              value={
                request?.type === "Test Drive"
                  ? formatDate(request?.createdAt)
                  : request?.createdAt
                  ? formatDate(request.createdAt)
                  : "hello"
              }
            />
            {request?.type === "Test Drive" && request?.time && (
              <InfoRow label="Time" value={request.time} />
            )}
            <InfoRow
              label="Status"
              value={
                <Chip
                  label={request?.status || "Unknown"}
                  color={
                    request?.status === "Assigned"
                      ? "success"
                      : request?.status === "Pending"
                      ? "warning"
                      : "error"
                  }
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              }
            />
            {request?.type !== "Test Drive" && request?.totalAmount && (
              <InfoRow
                label="Total Amount"
                value={`${request.totalAmount?.toLocaleString("en-IN", {
                  currency: "INR",
                  style: "currency",
                  maximumFractionDigits: 0,
                })}`}
              />
            )}
          </InfoCard>

          <InfoCard title="Customer Information">
            <InfoRow
              label="Customer Name"
              value={user?.firstName || "Not provided"}
            />
            <InfoRow label="Email" value={user?.email || "N/A"} />
            <InfoRow label="Phone" value={user?.phone || "N/A"} />
            <InfoRow label="Role" value={user?.role || "N/A"} />
          </InfoCard>
        </Stack>

        {/* RIGHT COLUMN CARDS */}
        <Stack spacing={3} sx={{ height: "100%" }}>
          {request?.type === "Test Drive" ? (
            <InfoCard title="Test Drive Person">
              <InfoRow
                label="Name"
                value={`${request?.firstName} ${request?.lastName}`}
              />
              <InfoRow label="Email" value={request?.email} />
              <InfoRow label="Phone Number" value={request?.phoneNumber} />
              {/* <InfoRow
                label="Address"
                value={`${request?.address}, ${request?.city}`}
              /> */}
            </InfoCard>
          ) : (
            <InfoCard title="Salesperson Assigned">
              {assignedSalesperson?.firstName ? (
                <>
                  <InfoRow
                    label="Name"
                    value={assignedSalesperson?.firstName || "N/A"}
                  />
                  <InfoRow
                    label="Phone Number"
                    value={assignedSalesperson?.phone || "N/A"}
                  />
                  <InfoRow
                    label="Email"
                    value={assignedSalesperson?.email || "N/A"}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No salesperson assigned yet
                </Typography>
              )}
            </InfoCard>
          )}

          <InfoCard title="Car Information">
            {car?.brand || car?.model ? (
              <>
                {/* <InfoRow label="Car ID" value={car?.id || "N/A"} /> */}
                <InfoRow label="Brand" value={car?.brand?.name || "N/A"} />
                <InfoRow label="Car Model" value={car?.model?.name || "N/A"} />
                <InfoRow label="Variant" value={car?.variant || "N/A"} />
                <InfoRow label="Engine Type" value={car?.engineType || "N/A"} />
                {car?.year && (
                  <InfoRow label="Year" value={car.year.toString()} />
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No car information available
              </Typography>
            )}
          </InfoCard>
        </Stack>
      </Box>
    </Box>
  );
};
